
import { Ionicons } from "@expo/vector-icons";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import type { LinkingOptions } from "@react-navigation/native";
import { getFocusedRouteNameFromRoute, useNavigation } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState, useEffect } from "react";
import { Platform, Text, TouchableOpacity, View } from "react-native";
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAppTheme } from '../design-system/ThemeProvider';
import { spacing, radius } from '../design-system/tokens';
import { LocalizationDashboard } from "../features/admin/components/LocalizationDashboard";
import { AchievementsScreen } from "../features/gamification/components/AchievementsScreen";
import { GamificationDashboard } from "../features/gamification/components/GamificationDashboard";
import { StreakCalendarScreen } from "../features/gamification/components/StreakCalendarScreen";
import { XPLeaderboardScreen } from "../features/gamification/components/XPLeaderboardScreen";
import { CommunityTranslationScreen } from "../features/localization/components/CommunityTranslationScreen";
import { SubscriptionPlansModal } from "../features/monetization/components/SubscriptionPlansModal";
import { SubscriptionScreenRouter } from "../features/monetization/components/SubscriptionScreenRouter";
import { entitlementService } from "../features/monetization/services/entitlementService";
import { consumePendingPremiumCheckout } from "../features/monetization/services/pendingPremiumCheckout";
import { revenueCatService } from "../features/monetization/services/revenueCatService";
import { subscriptionStateManager } from "../features/monetization/services/subscriptionStateManager";
import RoomInviteLandingScreen from "../features/multi-player/screens/RoomInviteLandingScreen";
import { useLanguageStore } from "../features/quiz-core/languageStore";
import { QuizLandingPage } from '../features/whatsapp-engagement/screens/QuizLandingPage';
import { t } from "../i18n/translations";
import { isScreenshotMode } from "../../lib/screenshotMode";
import { useAuth } from "../shared/hooks/useAuth";
import { iqesColors } from "../shared/ui/iqesDesignSystem";

import AuthGuard from "./AuthGuard";
import MoreNavigator from './MoreNavigator';
import PlayNavigator from './PlayNavigator';
import TogetherNavigator from './TogetherNavigator';

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();

type AppLinkingParamList = Record<string, object | undefined>;

type TabName = 'Play' | 'Together' | 'Me';

type NestedNavigationState = {
  index?: number;
  routes?: Array<{
    name: string;
    state?: NestedNavigationState;
  }>;
};

type TabNavigationState = {
  routes?: Array<{
    name: TabName;
    state?: NestedNavigationState;
  }>;
};

type TabIconName = keyof typeof Ionicons.glyphMap;

const getDeepestRouteName = (state?: NestedNavigationState): string | null => {
  if (!state?.routes?.length) {
    return null;
  }

  const activeIndex = typeof state.index === 'number' ? state.index : 0;
  const activeRoute = state.routes[activeIndex] || state.routes[0];
  return getDeepestRouteName(activeRoute.state) || activeRoute.name;
};

const kingdomStackHeaderOptions = (title: string) => ({
  headerShown: !isScreenshotMode(),
  title,
  headerStyle: {
    backgroundColor: iqesColors.canvas,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(246,190,58,0.28)',
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
    elevation: 4,
  },
  headerTintColor: iqesColors.goldBright,
  headerTitleStyle: {
    color: iqesColors.ivory,
    fontWeight: '900' as const,
  },
});

// Header right: tri-state (loading | premium | none).
// 'loading' reserves badge-width to prevent title shift on first paint.
type HeaderRightState = 'loading' | 'premium' | 'none';

// Shared so screenOptions can read it for marginRight.
let headerRightState: HeaderRightState = 'loading';

const HeaderRightButtons = () => {
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { user } = useAuth();
  const { colors } = useAppTheme();

  const formatPlanLabel = (planName?: string | null) => {
    if (!planName) return 'Premium';
    if (planName.toLowerCase() === 'premium') return 'Premium';
    return planName;
  };

  const checkPremiumStatus = async () => {
    if (!user?.id) {
      setCurrentPlan(null);
      headerRightState = 'none';
      setResolved(true);
      return;
    }

    try {
      // Identify with RevenueCat (non-fatal — subscription check should
      // still proceed via Supabase even if RevenueCat is unavailable).
      const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';
      if (isNativeMobile) {
        try {
          await revenueCatService.identifyUser(user.id);
        } catch (rcError) {
          console.warn('⚠️ [Premium] RevenueCat identify failed (non-fatal):', rcError);
        }
      }

      const subscription = await entitlementService.getUserSubscription(user.id);
      const hasActiveSubscription =
        subscription !== null &&
        ['active', 'trialing'].includes(subscription.status);

      if (!hasActiveSubscription) {
        setCurrentPlan(null);
        headerRightState = 'none';
      } else {
        const label = formatPlanLabel(subscription.subscriptionType);
        setCurrentPlan(label);
        headerRightState = 'premium';
      }
    } catch (error) {
      console.error('Failed to check premium status:', error);
      setCurrentPlan(null);
      headerRightState = 'none';
    }
    setResolved(true);
  };

  useEffect(() => {
    headerRightState = 'loading';
    setResolved(false);
    checkPremiumStatus();

    const unsubscribe = subscriptionStateManager.subscribe(checkPremiumStatus);
    const interval = setInterval(checkPremiumStatus, 120000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [user?.id]);

  return (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {/* Current Plan Display - Show for premium users only */}
        {currentPlan && (
          <TouchableOpacity
            onPress={() => setShowSubscriptionModal(true)}
            style={{
              marginRight: spacing.sm,
              overflow: 'hidden',
              borderRadius: radius.lg,
              shadowColor: iqesColors.gold,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.15,
              shadowRadius: 4,
              elevation: 3,
              minWidth: 108,
            }}
          >
            <LinearGradient
              colors={[iqesColors.green, iqesColors.greenDeep]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                paddingHorizontal: spacing.sm,
                paddingVertical: 5,
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: 26,
              }}
            >
              <Ionicons name="checkmark-circle" size={10} color={colors.white} style={{ marginRight: 3 }} />
              <Text style={{
                fontSize: 11,
                fontWeight: '700',
                color: colors.white,
                letterSpacing: 0.2,
              }} numberOfLines={1}>
                {currentPlan === 'Premium' ? 'Premium' : currentPlan}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        )}

        {/* Invisible spacer during loading to prevent title shift */}
        {!resolved && !currentPlan && (
          <View style={{ width: 120, marginRight: spacing.sm }} />
        )}
      </View>

      {showSubscriptionModal && (
        <SubscriptionPlansModal
          visible={showSubscriptionModal}
          onClose={() => {
            setShowSubscriptionModal(false);
            checkPremiumStatus();
          }}
        />
      )}
    </>
  );
};

// Create a header component that's reactive to language changes - UPDATED
const NavigationHeader = ({ activeTab }: { activeTab: string }) => {
  const { language } = useLanguageStore();
  const titleMap: Record<string, string> = {
    Play: t('play', language),
    Together: t('together', language),
    Me: t('me', language),
  };

  return (
    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ fontSize: 18, fontWeight: '900', color: iqesColors.ivory, letterSpacing: 0.2 }}>
        {titleMap[activeTab] || activeTab}
      </Text>
    </View>
  );
};

const MainTabs = () => {
  const { language } = useLanguageStore();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const navigation = useNavigation();

  useEffect(() => {
    let isCancelled = false;

    const resumePendingPremiumCheckout = async () => {
      if (!user?.id) {
        return;
      }

      const pendingCheckout = await consumePendingPremiumCheckout();
      if (!pendingCheckout || isCancelled) {
        return;
      }

      const parentNavigation = navigation.getParent() ?? navigation;
      parentNavigation.navigate('SubscriptionPlans' as never, {
        preselectedPlan: pendingCheckout.preselectedPlan,
        preselectedProductId: pendingCheckout.preselectedProductId,
        resumeAfterAuth: true,
      } as never);
    };

    void resumePendingPremiumCheckout();

    return () => {
      isCancelled = true;
    };
  }, [navigation, user?.id]);

  // Shared header configuration for consistent navigation
  const getSharedHeaderOptions = (activeTab: TabName) => {
    return {
      headerShown: true,
      headerTitle: () => <NavigationHeader activeTab={activeTab} />,
      headerStyle: {
      backgroundColor: iqesColors.canvas,
      borderBottomWidth: 1,
      borderBottomColor: 'rgba(246,190,58,0.28)',
      shadowColor: iqesColors.gold,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 4,
      elevation: 4,
      height: 70 + insets.top, // Include safe area for notch/Dynamic Island
    },
    headerTitleContainerStyle: {
      flex: 1,
      // Reserve space for the right-side Premium badge (or loading spacer).
      // 'none' state has no right content, so minimal margin.
      marginRight: headerRightState === 'none' ? spacing.sm : 120,
      paddingHorizontal: spacing.sm,
    },
    headerRightContainerStyle: {
      paddingRight: spacing.sm,
    },
    headerRight: () => <HeaderRightButtons />,
    };
  };

  return (
    <>
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color }) => {
          let iconName: TabIconName = 'play-outline';
          if (route.name === "Play") {
            iconName = focused ? "play" : "play-outline";
          } else if (route.name === "Together") {
            iconName = focused ? "people" : "people-outline";
          } else if (route.name === "Me") {
            iconName = focused ? "person" : "person-outline";
          }
          return (
            <Ionicons
              name={iconName}
              size={26}
              color={color}
              data-oid="up7vx._"
            />
          );
        },
        // Improved text styling for better readability
        tabBarLabelStyle: {
          fontSize: 14,
          fontWeight: '700',
          marginTop: 2,
          textAlign: 'center'
        },
        // Enhanced tab bar styling with better colors and spacing
        tabBarStyle: {
          height: 65 + Math.max(insets.bottom, spacing.sm),
          paddingBottom: Math.max(spacing.md, insets.bottom),
          paddingTop: spacing.sm,
          backgroundColor: iqesColors.canvas,
          borderTopWidth: 1,
          borderTopColor: 'rgba(246,190,58,0.32)',
          shadowColor: iqesColors.gold,
          shadowOffset: { width: 0, height: -2 },
          shadowOpacity: 0.14,
          shadowRadius: 3,
          elevation: 8,
        },
        tabBarHideOnKeyboard: true,
        // Keep labels/icons legible across light and dark shells.
        tabBarActiveTintColor: iqesColors.goldBright,
        tabBarInactiveTintColor: 'rgba(255,248,232,0.64)',
        // Better label positioning
        tabBarLabelPosition: 'below-icon',
        tabBarAllowFontScaling: true, // Allow system font scaling for accessibility
      })}
      data-oid="p4gjqhr"
    >
      {/* Tab 1: Play - Solo study and quiz flow */}
      <Tab.Screen
        name="Play"
        component={PlayNavigator}
        options={({ navigation, route }) => {
          // Get current navigation state to detect current screen
          const navigationState = navigation.getState() as TabNavigationState;

          // Get the active route in the Play stack
          let currentRouteName = getFocusedRouteNameFromRoute(route) || 'Category'; // Default fallback
          currentRouteName = getDeepestRouteName(route.state as NestedNavigationState | undefined) || currentRouteName;
          try {
            const playStackState = navigationState.routes?.find((route) => route.name === 'Play')?.state;
            currentRouteName = getDeepestRouteName(playStackState) || currentRouteName;
          } catch (error) {
            console.warn('Error getting current route:', error);
          }

          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            if (window.location.pathname.includes('/Play/Quiz')) {
              currentRouteName = 'SinglePlayerGame';
            } else if (window.location.pathname.includes('/Play/IQES')) {
              currentRouteName = 'IQESPrototype';
            } else if (window.location.pathname.includes('/Play/Results')) {
              currentRouteName = 'SinglePlayerResultsScreen';
            } else if (window.location.pathname.includes('/Play/Submitting')) {
              currentRouteName = 'SinglePlayerSubmittingScreen';
            }
          }

          // Define screen types
          const isOnTestamentScreen = currentRouteName === 'Testament';
          const isOnBookSelectionScreen = currentRouteName === 'BookSelection';
          const isOnDifficultyScreen = currentRouteName === 'Difficulty';
          const isOnCategoryScreen = currentRouteName === 'Category';
          const isOnGameScreen = currentRouteName === 'SinglePlayerGame';
          const isOnIQESPrototypeScreen = currentRouteName === 'IQESPrototype';
          const isOnResultsScreen = currentRouteName === 'SinglePlayerResultsScreen';
          const isOnSubmittingScreen = currentRouteName === 'SinglePlayerSubmittingScreen';

          if (isOnIQESPrototypeScreen) {
            return {
              tabBarLabel: t('play', language),
              headerShown: false,
              tabBarStyle: { display: 'none' as const },
            };
          }

          if (isOnGameScreen || isOnResultsScreen || isOnSubmittingScreen) {
            return {
              tabBarLabel: t('play', language),
              headerShown: false,
              tabBarStyle: { display: 'none' as const },
            };
          }

          const needsBackArrow = !isOnCategoryScreen && navigation.canGoBack();

          return {
            tabBarLabel: t('play', language),
            headerShown: true,
            headerTitle: () => {
              // Show context title above tabs for deeper screens
              const contextTitle = needsBackArrow ?
                (isOnTestamentScreen ? "Choose Testament" :
                 isOnBookSelectionScreen ? "Select Book" :
                 isOnDifficultyScreen ? "Select Difficulty" :
                 isOnGameScreen ? "Quiz in Progress" :
                 isOnResultsScreen ? "Quiz Results" :
                 isOnSubmittingScreen ? "Submitting..." : null) : null;

              return (
                <View style={{ alignItems: 'center', flexShrink: 1, maxWidth: '100%' }}>
                  {/* Context title for deeper screens */}
                  {contextTitle && (
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '500',
                      color: 'rgba(255,248,232,0.62)',
                      marginBottom: spacing.xs
                    }}>
                      {contextTitle}
                    </Text>
                  )}

                  <Text style={{
                    fontSize: contextTitle ? 16 : 18,
                    fontWeight: '700',
                    color: iqesColors.ivory,
                  }}>
                    {t('play', language)}
                  </Text>
                </View>
              );
            },
            headerStyle: {
              backgroundColor: iqesColors.canvas,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(246,190,58,0.28)',
              shadowColor: iqesColors.gold,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.12,
              shadowRadius: 4,
              elevation: 4,
              height: (needsBackArrow ? 85 : 70) + insets.top, // Extra height for context title + safe area
            },
            // Reserve space for the right-side Premium badge (or loading spacer).
            headerTitleContainerStyle: {
              flex: 1,
              marginRight: headerRightState === 'none' ? spacing.sm : 120,
              paddingHorizontal: spacing.sm,
            },
            headerRightContainerStyle: {
              paddingRight: spacing.sm,
            },
            headerLeft: needsBackArrow ? () => (
              <TouchableOpacity
                onPress={() => {
                  try {
                    // Check if we can go back
                    if (navigation.canGoBack()) {
                      navigation.goBack();
                    } else {
                      // Fallback: Navigate to main category screen
                      navigation.navigate('Play', {
                        screen: 'Category'
                      });
                    }
                  } catch (error) {
                    console.warn('Navigation back failed:', error);
                    // Double fallback: Reset to main Play screen
                    navigation.navigate('Play');
                  }
                }}
                style={{
                  marginLeft: spacing.lg,
                  padding: spacing.sm,
                  borderRadius: radius.xl,
                  backgroundColor: 'rgba(8,10,24,0.88)',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}
              >
                <Ionicons name="arrow-back" size={20} color={iqesColors.goldBright} />
              </TouchableOpacity>
            ) : undefined,
            headerRight: () => <HeaderRightButtons />
          };
        }}
        data-oid="play-tab"
      />

      {/* Tab 2: Together - private family and friends play */}
      <Tab.Screen
        name="Together"
        component={TogetherNavigator}
        options={() => ({
          tabBarLabel: t('together', language),
          ...getSharedHeaderOptions('Together')
        })}
        data-oid="together-tab"
      />

      {/* Tab 3: Me - Profile, progress, settings */}
      <Tab.Screen
        name="Me"
        component={MoreNavigator}
        options={() => ({
          tabBarLabel: t('me', language),
          ...getSharedHeaderOptions('Me')
        })}
        data-oid="me-tab"
      />
    </Tab.Navigator>
    </>
  );
};

// Deep linking configuration
const linking: LinkingOptions<AppLinkingParamList> = {
  prefixes: [
    "kingdombiblequiz://",
    "https://kingdombiblequiz.com",
    "https://www.kingdombiblequiz.com",
  ],
  config: {
    screens: {
      QuizLanding: "app/quiz/:sessionId",
      RoomInviteLanding: "join/room/:roomId",
      AuthenticatedApp: {
        screens: {
          Main: {
            screens: {
              Play: {
                path: "Play",
                screens: {
                  Category: "",
                  Testament: "Testament",
                  BookSelection: "BookSelection",
                  Difficulty: "Difficulty",
                  IQESPrototype: "IQES",
                  SinglePlayerGame: "Quiz",
                  SinglePlayerSubmittingScreen: "Submitting",
                  SinglePlayerResultsScreen: "Results",
                },
              },
              Together: {
                path: "Together",
                screens: {
                  FamilyHub: {
                    path: "",
                    screens: {
                      FamilyHub: "",
                      FriendsHub: "Friends",
                      MultiplayerLobby: "Lobby",
                      MultiplayerCategory: "Category",
                      MultiplayerTestament: "Testament",
                      MultiplayerBookSelection: "BookSelection",
                      MultiplayerDifficulty: "Difficulty",
                      GameRoom: "Room",
                      MultiplayerQuiz: "Quiz",
                      MultiplayerResults: "Results",
                      WaitingForOpponent: "Waiting",
                    },
                  },
                },
              },
              Me: {
                path: "Me",
                screens: {
                  MoreMenu: "",
                  ProfileDetail: "Profile",
                  GamificationDetail: "Progress",
                  LeaderboardDetail: "Leaderboard",
                  SettingsDetail: "Settings",
                  About: "About",
                },
              },
              Gamification: {
                screens: {
                  GamificationDashboard: "progress",
                  Achievements: "achievements",
                  StreakCalendar: "streak-calendar",
                  XPLeaderboard: "leaderboard",
                },
              },
            },
          },
          GameRoom: "join/:roomId",
          MainGamificationDashboard: "main-progress",
          MainAchievements: "main-achievements",
          MainStreakCalendar: "main-streak-calendar",
          MainXPLeaderboard: "main-leaderboard",
        },
      },
    },
  },
};

const AppNavigator = () => {
  return (
    <Stack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="AuthenticatedApp"
      data-oid="mu:wso1"
    >
      {/* Quiz Landing Page - BYPASSES AUTHENTICATION */}
      <Stack.Screen
        name="QuizLanding"
        component={QuizLandingPage}
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <Stack.Screen
        name="RoomInviteLanding"
        component={RoomInviteLandingScreen}
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />

      {/* Main app with authentication */}
      <Stack.Screen name="AuthenticatedApp" data-oid="3ch3d94">
        {() => (
          <AuthGuard data-oid="chjcblk">
            <Stack.Navigator
              screenOptions={{ headerShown: false }}
              data-oid="auth-stack"
            >
              <Stack.Screen name="Main" component={MainTabs} />

              {/* Phase 5 - Deep Localization Screens */}
              <Stack.Screen
                name="CommunityTranslation"
                component={CommunityTranslationScreen}
                options={{ presentation: 'modal' }}
              />
              <Stack.Screen
                name="LocalizationDashboard"
                component={LocalizationDashboard}
                options={{ presentation: 'modal' }}
              />

              {/* Phase 6 - Monetization Screens */}
              <Stack.Screen
                name="SubscriptionPlans"
                component={SubscriptionScreenRouter}
                options={{
                  presentation: 'modal',
                  headerShown: false,
                  title: 'Premium Plans'
                }}
              />
              {/* Gamification Screens for Deep Linking */}
              <Stack.Screen
                name="MainGamificationDashboard"
                component={GamificationDashboard}
                options={kingdomStackHeaderOptions('Progress Dashboard')}
              />
              <Stack.Screen
                name="MainAchievements"
                component={AchievementsScreen}
                options={kingdomStackHeaderOptions('Achievements')}
              />
              <Stack.Screen
                name="MainStreakCalendar"
                component={StreakCalendarScreen}
                options={kingdomStackHeaderOptions('Streak Calendar')}
              />
              <Stack.Screen
                name="MainXPLeaderboard"
                component={XPLeaderboardScreen}
                options={kingdomStackHeaderOptions('XP Leaderboard')}
              />
            </Stack.Navigator>
          </AuthGuard>
        )}
      </Stack.Screen>
    </Stack.Navigator>
  );
};

export default AppNavigator;
export { linking };
